(() => {
  if (window.AbokProductCommerce) return;

  const roleAttribute = '_abok_role';
  const parentAttribute = '_abok_parent_variant';
  const offerAttribute = '_abok_offer_key';
  const requestAttribute = '_abok_request_id';

  function variantGid(value) {
    const id = String(value || '').trim();
    return id.startsWith('gid://shopify/ProductVariant/') ? id : `gid://shopify/ProductVariant/${id}`;
  }

  function integerValue(input, fallback = 1) {
    const value = Number.parseInt(input?.value ?? input, 10);
    return Number.isFinite(value) ? value : fallback;
  }

  function clampQuantity(input, nextValue) {
    const minimum = integerValue(input.min, 1);
    const maximum = integerValue(input.max, Number.MAX_SAFE_INTEGER);
    const step = Math.max(1, integerValue(input.step, 1));
    const clamped = Math.min(maximum, Math.max(minimum, integerValue(nextValue, minimum)));
    const stepped = minimum + Math.round((clamped - minimum) / step) * step;
    input.value = String(Math.min(maximum, Math.max(minimum, stepped)));
    return integerValue(input.value, minimum);
  }

  function formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) return '0';

    const [whole, fraction] = amount.toFixed(precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return precision > 0 ? `${grouped}${decimal}${fraction}` : grouped;
  }

  function formatShopifyMoney(cents, format) {
    const match = String(format || '').match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return '';

    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_space_separator: [2, ' ', ','],
    };
    const [precision, thousands, decimal] = formats[match[1]] || formats.amount;
    return format.replace(match[0], formatWithDelimiters(cents, precision, thousands, decimal));
  }

  function lineAttributes(line) {
    return Object.fromEntries((line?.attributes || []).map(({ key, value }) => [key, value]));
  }

  function giftLine(snapshot, claim) {
    return (snapshot?.lines || []).find((line) => {
      const attributes = lineAttributes(line);
      return attributes[roleAttribute] === 'gift'
        && attributes[offerAttribute] === claim.offerKey
        && attributes[requestAttribute] === claim.requestId;
    });
  }

  function giftIsFullyDiscounted(line) {
    return Boolean(
      line
      && Math.abs(Number(line.cost?.totalAmount?.amount || 0)) < 0.000001
      && Array.isArray(line.discountAllocations)
      && line.discountAllocations.length > 0
    );
  }

  class AbokPurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.productInfo = this.closest('product-info');
      this.tabs = [...this.querySelectorAll('[data-abok-purchase-tab]')];
      this.panels = [...this.querySelectorAll('[data-abok-purchase-panel]')];
      const options = { signal: this.abortController.signal };

      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('change', (event) => this.onChange(event), options);
      this.addEventListener('input', (event) => this.onInput(event), options);
      this.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), options);
      this.productInfo?.addEventListener('change', () => this.updateSummary(), options);
      this.productInfo?.addEventListener('input', () => this.updateSummary(), options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const sectionId = this.productInfo?.dataset.originalSection || this.productInfo?.dataset.section;
          if (data?.sectionId === sectionId) this.updateSummary();
        });
      }
      this.updateSummary();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.variantChangeUnsubscriber?.();
      this.variantChangeUnsubscriber = null;
    }

    onClick(event) {
      const tab = event.target.closest('[data-abok-purchase-tab]');
      if (tab) {
        this.activateTab(tab, true);
        return;
      }

      const button = event.target.closest('[data-abok-bundle-decrease], [data-abok-bundle-increase]');
      if (!button) return;

      const item = button.closest('[data-abok-bundle-item]');
      const input = item?.querySelector('[data-abok-bundle-quantity-input]');
      const toggle = item?.querySelector('[data-abok-bundle-toggle]');
      if (!item || !input || !toggle) return;

      const direction = button.hasAttribute('data-abok-bundle-increase') ? 1 : -1;
      clampQuantity(input, integerValue(input) + direction * Math.max(1, integerValue(input.step, 1)));
      toggle.checked = true;
      this.syncItem(item);
      this.notifySelection('bundle_quantity', item);
    }

    onChange(event) {
      const item = event.target.closest('[data-abok-bundle-item]');
      if (!item) return;

      const input = item.querySelector('[data-abok-bundle-quantity-input]');
      if (event.target === input) clampQuantity(input, input.value);
      this.syncItem(item);
      this.notifySelection(event.target.matches('[data-abok-bundle-toggle]') ? 'bundle_selection' : 'bundle_quantity', item);
    }

    onInput(event) {
      if (!event.target.matches('[data-abok-bundle-quantity-input]')) return;
      const item = event.target.closest('[data-abok-bundle-item]');
      const toggle = item?.querySelector('[data-abok-bundle-toggle]');
      if (toggle) toggle.checked = true;
      this.syncItem(item);
    }

    onKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.target);
      if (currentIndex < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      this.activateTab(this.tabs[nextIndex], true);
    }

    activateTab(nextTab, focus = false) {
      this.tabs.forEach((tab) => {
        const selected = tab === nextTab;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
        if (panel) panel.hidden = !selected;
      });
      if (focus) nextTab.focus();
    }

    onBlockSelect(event) {
      const panel = event.target.closest?.('[data-abok-purchase-panel]');
      if (!panel || !this.contains(panel)) return;

      const tab = this.tabs.find((candidate) => candidate.getAttribute('aria-controls') === panel.id);
      if (tab) this.activateTab(tab, false);
    }

    syncItem(item) {
      const selected = Boolean(item.querySelector('[data-abok-bundle-toggle]')?.checked);
      item.toggleAttribute('data-selected', selected);
      this.updateSummary();
    }

    updateSummary() {
      const selectedItems = [...this.querySelectorAll('[data-abok-bundle-item][data-selected]')];
      const bundleCents = selectedItems.reduce((total, item) => {
        const price = integerValue(item.dataset.priceCents, 0);
        const quantity = integerValue(item.querySelector('[data-abok-bundle-quantity-input]'), 1);
        return total + price * quantity;
      }, 0);
      const bundle = this.querySelector('[data-abok-product-bundle]');
      const variantSelects = this.productInfo?.querySelector('variant-selects');
      const selectedPriceAmount = variantSelects?.dataset.selectedPriceAmount;
      const mainAmount = Number(selectedPriceAmount);
      const hasSelectedPrice = selectedPriceAmount !== undefined
        && selectedPriceAmount !== ''
        && Number.isFinite(mainAmount);
      const mainQuantity = integerValue(this.productInfo?.querySelector('input[name="quantity"]'), 1);
      const fallbackMainCents = integerValue(bundle?.dataset.mainPriceCents, 0);
      const mainCents = (hasSelectedPrice ? Math.round(mainAmount * 100) : fallbackMainCents) * mainQuantity;
      const totalCents = mainCents + bundleCents;

      const count = this.querySelector('[data-abok-bundle-count]');
      const bundleTotal = this.querySelector('[data-abok-bundle-total]');
      const combinedTotal = this.querySelector('[data-abok-combined-total]');
      if (count) count.textContent = String(selectedItems.length);
      if (bundleTotal) bundleTotal.textContent = this.formatMoney(bundleCents);
      if (combinedTotal) combinedTotal.textContent = this.formatMoney(totalCents);
      this.updatePurchasePrice(totalCents);
    }

    updatePurchasePrice(totalCents) {
      const region = this.productInfo?.querySelector('[data-abok-purchase-price]');
      const price = region?.querySelector('.price');
      if (!price) return;

      const currentPrice = price.classList.contains('price--on-sale')
        ? price.querySelector('.price__sale .price-item--last')
        : price.querySelector('.price__regular .price-item--regular');
      if (currentPrice) currentPrice.textContent = this.formatMoney(totalCents);
    }

    formatMoney(cents) {
      const moneyFormat = this.dataset.currencyCodeEnabled === 'true'
        ? this.dataset.moneyWithCurrencyFormat
        : this.dataset.moneyFormat;
      const formattedMoney = formatShopifyMoney(cents, moneyFormat);
      if (formattedMoney) return formattedMoney;

      const currencyCode = this.dataset.currencyCode
        || this.querySelector('[data-abok-product-bundle]')?.dataset.currencyCode
        || window.storefrontCartConfig?.currencyCode
        || 'USD';
      const money = { amount: (cents / 100).toFixed(2), currencyCode };
      if (window.AbokStorefrontCart?.formatMoney) return window.AbokStorefrontCart.formatMoney(money);
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode,
      }).format(cents / 100);
    }

    notifySelection(type, item) {
      this.dispatchEvent(new CustomEvent('abok:commerce-selection', {
        bubbles: true,
        detail: {
          type,
          stableKey: item.dataset.stableKey || '',
          variantId: item.dataset.variantId || '',
          selected: item.hasAttribute('data-selected'),
          quantity: integerValue(item.querySelector('[data-abok-bundle-quantity-input]'), 1),
        },
      }));
    }
  }

  class AbokFreeGift extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.toggle = this.querySelector('[data-abok-gift-toggle]');
      this.toggle?.addEventListener('change', () => this.syncState(true), {
        signal: this.abortController.signal,
      });
      this.syncState(false);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    syncState(notify) {
      const selected = Boolean(this.toggle?.checked);
      this.dataset.selected = String(selected);
      if (!notify) return;
      this.dispatchEvent(new CustomEvent('abok:commerce-selection', {
        bubbles: true,
        detail: { type: 'gift_selection', variantId: this.dataset.variantId || '', selected, quantity: 1 },
      }));
    }

    setSettlementState(state, message) {
      this.dataset.state = state;
      const status = this.querySelector('[data-abok-gift-status]');
      if (status) status.textContent = message || '';
      if (state === 'removed' && this.toggle) {
        this.toggle.checked = false;
        this.syncState(false);
      }
    }
  }

  function collectPurchaseLines(event) {
    const detail = event.detail;
    const productInfo = event.target.closest?.('product-info');
    if (!detail || !productInfo || !Array.isArray(detail.extraLines) || !Array.isArray(detail.claims)) return;

    const parentVariant = variantGid(detail.mainVariantId);
    productInfo.querySelectorAll('[data-abok-bundle-item][data-selected]').forEach((item) => {
      detail.extraLines.push({
        merchandiseId: item.dataset.variantId,
        quantity: integerValue(item.querySelector('[data-abok-bundle-quantity-input]'), 1),
        attributes: {
          [roleAttribute]: 'bundle',
          [parentAttribute]: parentVariant,
          [requestAttribute]: detail.requestId,
        },
      });
    });

    const gift = productInfo.querySelector('[data-abok-free-gift]');
    if (!gift?.querySelector('[data-abok-gift-toggle]')?.checked) return;

    const offerKey = String(gift.dataset.offerKey || '').trim();
    const giftVariantId = String(gift.dataset.variantId || '').trim();
    if (!offerKey || !giftVariantId) return;

    detail.extraLines.push({
      merchandiseId: giftVariantId,
      quantity: 1,
      attributes: {
        [roleAttribute]: 'gift',
        [parentAttribute]: parentVariant,
        [offerAttribute]: offerKey,
        [requestAttribute]: detail.requestId,
      },
    });
    detail.claims.push({
      type: 'gift',
      requestId: detail.requestId,
      offerKey,
      variantId: variantGid(giftVariantId),
      element: gift,
    });
  }

  async function settleGift(claim, snapshot, mode) {
    const element = claim.element;
    const line = giftLine(snapshot, claim);
    if (giftIsFullyDiscounted(line)) {
      element?.setSettlementState('confirmed', element.dataset.confirmedMessage);
      return { snapshot };
    }

    const CartError = window.AbokStorefrontCart?.StorefrontCartError || Error;
    if (mode === 'buy-now') {
      element?.setSettlementState('error', element.dataset.removedMessage);
      throw new CartError(element?.dataset.removedMessage || 'The gift discount could not be confirmed.', {
        code: 'GIFT_DISCOUNT_NOT_APPLIED',
      });
    }

    let nextSnapshot = snapshot;
    if (line?.id) {
      try {
        nextSnapshot = await window.AbokStorefrontCart.removeLine(line.id, { source: 'gift-compensation' });
      } catch (error) {
        element?.setSettlementState('error', element.dataset.reviewMessage);
        throw new CartError(element?.dataset.reviewMessage || 'Review the gift price in your cart before checkout.', {
          code: 'GIFT_REMOVAL_FAILED',
          details: error,
        });
      }
    }

    element?.setSettlementState('removed', element.dataset.removedMessage);
    return {
      snapshot: nextSnapshot,
      warning: element?.dataset.removedMessage || 'The gift was removed because its discount was not applied.',
    };
  }

  function validatePurchase(event) {
    const detail = event.detail;
    if (!detail || !Array.isArray(detail.claims) || !Array.isArray(detail.settlements)) return;
    detail.claims
      .filter((claim) => claim.type === 'gift')
      .forEach((claim) => detail.settlements.push(settleGift(claim, detail.snapshot, detail.mode)));
  }

  document.addEventListener('abok:purchase-lines:collect', collectPurchaseLines);
  document.addEventListener('abok:purchase:validate', validatePurchase);

  if (!customElements.get('abok-purchase-options')) {
    customElements.define('abok-purchase-options', AbokPurchaseOptions);
  }
  if (!customElements.get('abok-free-gift')) {
    customElements.define('abok-free-gift', AbokFreeGift);
  }

  window.AbokProductCommerce = Object.freeze({ version: 1 });
})();
